import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Alert,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import api, { enhancedApi } from '../api';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (name[0] || '?').toUpperCase();
};

const ManageAssignmentsDialog = ({ open, onClose, clientIdOrIds, onSave, clientName = '' }) => {
  const isBulk = Array.isArray(clientIdOrIds) && clientIdOrIds.length > 1;
  const clientIds = Array.isArray(clientIdOrIds) ? clientIdOrIds : (clientIdOrIds ? [clientIdOrIds] : []);
  const singleClientId = !Array.isArray(clientIdOrIds) ? clientIdOrIds : (clientIdOrIds?.[0] || null);

  const [allUsers, setAllUsers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [roleAssignments, setRoleAssignments] = useState({});
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchUsers, setSearchUsers] = useState('');
  const [searchTeams, setSearchTeams] = useState('');
  const [searchRoleUsers, setSearchRoleUsers] = useState('');
  const [leftCheckedUsers, setLeftCheckedUsers] = useState([]);
  const [rightCheckedUsers, setRightCheckedUsers] = useState([]);
  const [leftCheckedTeams, setLeftCheckedTeams] = useState([]);
  const [rightCheckedTeams, setRightCheckedTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchAll();
      setActiveTab(0);
    }
  }, [open]);

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, teamsRes, rolesRes] = await Promise.all([
        api.get('/api/multi-tenant/users/'),
        api.get('/api/multi-tenant/teams/'),
        enhancedApi.getOrganizationalRoles(),
      ]);
      setAllUsers(Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.results || []));
      setAllTeams(Array.isArray(teamsRes.data) ? teamsRes.data : (teamsRes.data?.data || []));
      setAllRoles(Array.isArray(rolesRes.data) ? rolesRes.data : (rolesRes.data?.data || []));

      const firstId = clientIds[0];
      if (firstId) {
        const [assignedUsersRes, assignedTeamsRes, roleAssignRes] = await Promise.all([
          api.get(`/api/multi-tenant/clients/${firstId}/assigned-users/`),
          api.get(`/api/multi-tenant/clients/${firstId}/assigned-teams/`),
          api.get(`/api/multi-tenant/clients/${firstId}/role-assignments/`),
        ]);
        setSelectedUserIds((assignedUsersRes.data?.assigned_users || []).map(u => u.id));
        setSelectedTeamIds((assignedTeamsRes.data?.assigned_teams || []).map(t => t.id));
        const ra = {};
        (roleAssignRes.data?.role_assignments || []).forEach(r => {
          ra[r.role_id] = r.user_ids || [];
        });
        setRoleAssignments(ra);
      } else {
        setRoleAssignments({});
        setSelectedUserIds([]);
        setSelectedTeamIds([]);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = allUsers.filter(u =>
    (u.username || u.email || '').toLowerCase().includes(searchUsers.toLowerCase())
  );
  const filteredTeams = allTeams.filter(t =>
    (t.name || '').toLowerCase().includes(searchTeams.toLowerCase())
  );

  const filteredUsersForRoles = allUsers.filter(u =>
    (u.username || u.email || '').toLowerCase().includes(searchRoleUsers.toLowerCase())
  );

  const availableUsers = filteredUsers.filter(u => !selectedUserIds.includes(u.id));
  const assignedUsers = filteredUsers.filter(u => selectedUserIds.includes(u.id));
  const availableTeams = filteredTeams.filter(t => !selectedTeamIds.includes(t.id));
  const assignedTeams = filteredTeams.filter(t => selectedTeamIds.includes(t.id));

  const handleToggleUser = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleToggleTeam = (teamId) => {
    setSelectedTeamIds(prev =>
      prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]
    );
  };

  const handleToggleRoleUser = (roleId, userId) => {
    setRoleAssignments(prev => {
      const ids = prev[roleId] || [];
      const next = ids.includes(userId) ? ids.filter(id => id !== userId) : [...ids, userId];
      return { ...prev, [roleId]: next };
    });
  };

  const handleToggleLeftUsers = (userId) => {
    setLeftCheckedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleToggleRightUsers = (userId) => {
    setRightCheckedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleToggleLeftTeams = (teamId) => {
    setLeftCheckedTeams(prev =>
      prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]
    );
  };

  const handleToggleRightTeams = (teamId) => {
    setRightCheckedTeams(prev =>
      prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]
    );
  };

  const handleMoveUsersRight = () => {
    setSelectedUserIds(prev => [...new Set([...prev, ...leftCheckedUsers])]);
    setLeftCheckedUsers([]);
  };

  const handleMoveUsersLeft = () => {
    setSelectedUserIds(prev => prev.filter(id => !rightCheckedUsers.includes(id)));
    setRightCheckedUsers([]);
  };

  const handleMoveAllUsersRight = () => {
    setSelectedUserIds(prev => [...new Set([...prev, ...availableUsers.map(u => u.id)])]);
    setLeftCheckedUsers([]);
  };

  const handleMoveAllUsersLeft = () => {
    setSelectedUserIds(prev => prev.filter(id => !assignedUsers.map(u => u.id).includes(id)));
    setRightCheckedUsers([]);
  };

  const handleMoveTeamsRight = () => {
    setSelectedTeamIds(prev => [...new Set([...prev, ...leftCheckedTeams])]);
    setLeftCheckedTeams([]);
  };

  const handleMoveTeamsLeft = () => {
    setSelectedTeamIds(prev => prev.filter(id => !rightCheckedTeams.includes(id)));
    setRightCheckedTeams([]);
  };

  const handleMoveAllTeamsRight = () => {
    setSelectedTeamIds(prev => [...new Set([...prev, ...availableTeams.map(t => t.id)])]);
    setLeftCheckedTeams([]);
  };

  const handleMoveAllTeamsLeft = () => {
    setSelectedTeamIds(prev => prev.filter(id => !assignedTeams.map(t => t.id).includes(id)));
    setRightCheckedTeams([]);
  };

  const handleSelectAllLeftUsers = () => {
    if (leftCheckedUsers.length === availableUsers.length) {
      setLeftCheckedUsers([]);
    } else {
      setLeftCheckedUsers(availableUsers.map(u => u.id));
    }
  };

  const handleSelectAllRightUsers = () => {
    if (rightCheckedUsers.length === assignedUsers.length) {
      setRightCheckedUsers([]);
    } else {
      setRightCheckedUsers(assignedUsers.map(u => u.id));
    }
  };

  const handleSelectAllLeftTeams = () => {
    if (leftCheckedTeams.length === availableTeams.length) {
      setLeftCheckedTeams([]);
    } else {
      setLeftCheckedTeams(availableTeams.map(t => t.id));
    }
  };

  const handleSelectAllRightTeams = () => {
    if (rightCheckedTeams.length === assignedTeams.length) {
      setRightCheckedTeams([]);
    } else {
      setRightCheckedTeams(assignedTeams.map(t => t.id));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (isBulk || clientIds.length > 1) {
        for (const cid of clientIds) {
          await api.post(`/api/multi-tenant/clients/${cid}/assign-users/`, {
            user_ids: selectedUserIds,
            action: 'update',
          });
          await api.post(`/api/multi-tenant/clients/${cid}/assign-teams/`, {
            team_ids: selectedTeamIds,
            action: 'update',
          });
        }
        await enhancedApi.bulkUpdateClientRoleAssignments({
          client_ids: clientIds,
          role_assignments: roleAssignments,
        });
      } else if (singleClientId) {
        await api.post(`/api/multi-tenant/clients/${singleClientId}/assign-users/`, {
          user_ids: selectedUserIds,
          action: 'update',
        });
        await api.post(`/api/multi-tenant/clients/${singleClientId}/assign-teams/`, {
          team_ids: selectedTeamIds,
          action: 'update',
        });
        await enhancedApi.updateClientRoleAssignments(singleClientId, { role_assignments: roleAssignments });
      }
      onSave?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save assignments');
    } finally {
      setSaving(false);
    }
  };

  const displayLabel = isBulk
    ? `${clientIds.length} clients selected`
    : (clientName || `Client ${singleClientId}`);

  const TransferListColumn = ({ title, items, getItemId, getItemLabel, getItemAvatar, checkedIds, onToggle, onSelectAll, emptyMessage }) => (
    <Box sx={{ flex: 1, minWidth: 0, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 1.5, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {title} ({items.length})
        </Typography>
        {items.length > 0 && (
          <Checkbox
            size="small"
            checked={checkedIds.length === items.length && items.length > 0}
            indeterminate={checkedIds.length > 0 && checkedIds.length < items.length}
            onChange={onSelectAll}
            sx={{ p: 0 }}
          />
        )}
      </Box>
      <List dense sx={{ maxHeight: 260, overflow: 'auto' }}>
        {items.length === 0 ? (
          <ListItem>
            <ListItemText
              primary={emptyMessage}
              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            />
          </ListItem>
        ) : (
          items.map((item) => (
            <ListItem
              key={getItemId(item)}
              dense
              sx={{ py: 0.5, cursor: 'pointer' }}
              onClick={() => onToggle(getItemId(item))}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  size="small"
                  checked={checkedIds.includes(getItemId(item))}
                  onChange={() => onToggle(getItemId(item))}
                  onClick={(e) => e.stopPropagation()}
                />
              </ListItemIcon>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {getItemAvatar(item)}
              </ListItemIcon>
              <ListItemText
                primary={getItemLabel(item)}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
              />
            </ListItem>
          ))
        )}
      </List>
    </Box>
  );

  const TransferButtons = ({
    onMoveRight, onMoveLeft, onMoveAllRight, onMoveAllLeft,
    moveRightDisabled, moveLeftDisabled, moveAllRightDisabled, moveAllLeftDisabled
  }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5, px: 1 }}>
      <IconButton size="small" onClick={onMoveAllRight} disabled={moveAllRightDisabled} aria-label="Move all right">
        <KeyboardDoubleArrowRightIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={onMoveRight} disabled={moveRightDisabled} aria-label="Move right">
        <ChevronRightIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={onMoveLeft} disabled={moveLeftDisabled} aria-label="Move left">
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={onMoveAllLeft} disabled={moveAllLeftDisabled} aria-label="Move all left">
        <KeyboardDoubleArrowLeftIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
          Manage Assignments
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2, bgcolor: 'primary.50', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
            {displayLabel}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<PersonIcon sx={{ fontSize: 18 }} />}
              label={`${selectedUserIds.length} user${selectedUserIds.length !== 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
              color="primary"
            />
            <Chip
              icon={<GroupIcon sx={{ fontSize: 18 }} />}
              label={`${selectedTeamIds.length} team${selectedTeamIds.length !== 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
              color="primary"
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ m: 2, mb: 0 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tab label="Users" sx={{ fontSize: '0.875rem', textTransform: 'none', fontWeight: 500 }} />
              <Tab label="Teams" sx={{ fontSize: '0.875rem', textTransform: 'none', fontWeight: 500 }} />
              <Tab label="Role Assignments" sx={{ fontSize: '0.875rem', textTransform: 'none', fontWeight: 500 }} disabled={allRoles.length === 0} />
            </Tabs>

            {activeTab === 0 && (
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search users..."
                  value={searchUsers}
                  onChange={(e) => setSearchUsers(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'grey.500', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1 }}>
                  <TransferListColumn
                    title="Available"
                    items={availableUsers}
                    getItemId={(u) => u.id}
                    getItemLabel={(u) => u.username || u.email || 'Unknown'}
                    getItemAvatar={(u) => (
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                        {getInitials(u.username || u.email)}
                      </Avatar>
                    )}
                    checkedIds={leftCheckedUsers}
                    onToggle={handleToggleLeftUsers}
                    onSelectAll={handleSelectAllLeftUsers}
                    emptyMessage="No users available"
                  />
                  <TransferButtons
                    onMoveRight={handleMoveUsersRight}
                    onMoveLeft={handleMoveUsersLeft}
                    onMoveAllRight={handleMoveAllUsersRight}
                    onMoveAllLeft={handleMoveAllUsersLeft}
                    moveRightDisabled={leftCheckedUsers.length === 0}
                    moveLeftDisabled={rightCheckedUsers.length === 0}
                    moveAllRightDisabled={availableUsers.length === 0}
                    moveAllLeftDisabled={assignedUsers.length === 0}
                  />
                  <TransferListColumn
                    title="Assigned"
                    items={assignedUsers}
                    getItemId={(u) => u.id}
                    getItemLabel={(u) => u.username || u.email || 'Unknown'}
                    getItemAvatar={(u) => (
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                        {getInitials(u.username || u.email)}
                      </Avatar>
                    )}
                    checkedIds={rightCheckedUsers}
                    onToggle={handleToggleRightUsers}
                    onSelectAll={handleSelectAllRightUsers}
                    emptyMessage="No users assigned"
                  />
                </Box>
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search teams..."
                  value={searchTeams}
                  onChange={(e) => setSearchTeams(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'grey.500', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1 }}>
                  <TransferListColumn
                    title="Available"
                    items={availableTeams}
                    getItemId={(t) => t.id}
                    getItemLabel={(t) => t.name || 'Unknown'}
                    getItemAvatar={(t) => (
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'secondary.main' }}>
                        <GroupIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                    )}
                    checkedIds={leftCheckedTeams}
                    onToggle={handleToggleLeftTeams}
                    onSelectAll={handleSelectAllLeftTeams}
                    emptyMessage="No teams available"
                  />
                  <TransferButtons
                    onMoveRight={handleMoveTeamsRight}
                    onMoveLeft={handleMoveTeamsLeft}
                    onMoveAllRight={handleMoveAllTeamsRight}
                    onMoveAllLeft={handleMoveAllTeamsLeft}
                    moveRightDisabled={leftCheckedTeams.length === 0}
                    moveLeftDisabled={rightCheckedTeams.length === 0}
                    moveAllRightDisabled={availableTeams.length === 0}
                    moveAllLeftDisabled={assignedTeams.length === 0}
                  />
                  <TransferListColumn
                    title="Assigned"
                    items={assignedTeams}
                    getItemId={(t) => t.id}
                    getItemLabel={(t) => t.name || 'Unknown'}
                    getItemAvatar={(t) => (
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'secondary.main' }}>
                        <GroupIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                    )}
                    checkedIds={rightCheckedTeams}
                    onToggle={handleToggleRightTeams}
                    onSelectAll={handleSelectAllRightTeams}
                    emptyMessage="No teams assigned"
                  />
                </Box>
              </Box>
            )}

            {activeTab === 2 && allRoles.length > 0 && (
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search users..."
                  value={searchRoleUsers}
                  onChange={(e) => setSearchRoleUsers(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'grey.500', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select which team members fulfill each role for this client.
                </Typography>
                <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                  {allRoles.map((role) => {
                    const roleUserIds = roleAssignments[role.id] || [];
                    const count = roleUserIds.length;
                    return (
                      <Box
                        key={role.id}
                        sx={{
                          mb: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={role.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {count} member{count !== 1 ? 's' : ''} assigned
                          </Typography>
                        </Box>
                        <List dense>
                          {filteredUsersForRoles.length === 0 ? (
                            <ListItem>
                              <ListItemText primary="No users found" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                            </ListItem>
                          ) : (
                            filteredUsersForRoles.map((user) => (
                              <ListItem
                                key={user.id}
                                dense
                                sx={{ py: 0.5 }}
                                secondaryAction={
                                  <Checkbox
                                    size="small"
                                    checked={roleUserIds.includes(user.id)}
                                    onChange={() => handleToggleRoleUser(role.id, user.id)}
                                  />
                                }
                              >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                                    {getInitials(user.username || user.email)}
                                  </Avatar>
                                </ListItemIcon>
                                <ListItemText
                                  primary={user.username || user.email || 'Unknown'}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))
                          )}
                        </List>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageAssignmentsDialog;
